import React from 'react';
import {
	Autocomplete,
	Checkbox,
	FormControlLabel,
	Radio,
	RadioGroup,
	Table,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { TableHeadCellSx } from '../Table/EnhancedTableHead';

export interface FilterOption {
	label: string;
	name: string;
	// this will be ignored if using text
	options: string[];
	componentType: 'autocomplete' | 'text' | 'boolean'; // | 'select' | 'number' in future;
}

export interface Filter {
	enabled: boolean;
	value: string | boolean;
}

interface FilterTableProps {
	filterOptions: FilterOption[];
	filters: Record<string, Filter>;
	setFilters: (newFilters: Record<string, Filter>) => void;
	updateData: (newFilters: Record<string, Filter>) => void;
}

const FilterTable: React.FC<FilterTableProps> = ({ filterOptions, filters, setFilters, updateData }) => {
	const getFilterValue = (filterType: string): string | boolean | undefined => {
		if (filterType in filters) {
			return filters[filterType].value;
		}
	};

	const updateFilters = (filterType: string, newState: string | boolean) => {
		const newFilters = {
			...filters,
			[filterType]: {
				enabled: true,
				value: newState,
			},
		};

		setFilters(newFilters);
		updateData(newFilters);
	};

	const setFilterEnabled = (filterType: string, enabled: boolean) => {
		if (filterType in filters) {
			const newFilters = { ...filters };
			newFilters[filterType].enabled = enabled;

			if (!enabled && typeof newFilters[filterType].value === 'string') {
				newFilters[filterType].value = '';
			}

			setFilters(newFilters);
			updateData(newFilters);
		}
	};

	const getFilterEnabled = (filterType: string): boolean => {
		if (filterType in filters) {
			return filters[filterType].enabled;
		} else {
			return false;
		}
	};

	const getFilterComponent = (filterOption: FilterOption) => {
		switch (filterOption.componentType) {
			case 'autocomplete':
				return (
					<Autocomplete
						value={getFilterValue(filterOption.name)}
						onChange={(_, newVal) => updateFilters(filterOption.name, newVal || '')}
						options={filterOption.options}
						fullWidth
						disabled={!getFilterEnabled(filterOption.name)}
						renderInput={(params) => (
							<TextField
								{...params}
								slotProps={{
									htmlInput: { ...params.inputProps, 'aria-label': `${filterOption.label} Filter` },
								}}
							/>
						)}
						data-testid={`${filterOption.name}-filter-selector`}
					/>
				);
			case 'text':
				return (
					<TextField
						value={getFilterEnabled(filterOption.name) ? getFilterValue(filterOption.name) : ''}
						fullWidth
						disabled={!getFilterEnabled(filterOption.name)}
						onChange={(e) => updateFilters(filterOption.name, e.target.value || '')}
						data-testid={`${filterOption.name}-filter-textfield`}
					/>
				);
			case 'boolean':
				return (
					<RadioGroup
						row
						value={getFilterEnabled(filterOption.name) ? getFilterValue(filterOption.name) : null}
						onChange={(_, val) => updateFilters(filterOption.name, val === 'true')}
						sx={{ justifyContent: 'space-evenly' }}
					>
						<FormControlLabel
							value={true}
							control={<Radio />}
							label={filterOption.options.length > 0 ? filterOption.options[0] : 'True'}
							disabled={!getFilterEnabled(filterOption.name)}
							data-testid={`${filterOption.name}-filter-radio-true`}
						/>
						<FormControlLabel
							value={false}
							control={<Radio />}
							label={filterOption.options.length > 1 ? filterOption.options[1] : 'False'}
							disabled={!getFilterEnabled(filterOption.name)}
							data-testid={`${filterOption.name}-filter-radio-false`}
						/>
					</RadioGroup>
				);
		}
	};

	return (
		<Table>
			<TableHead>
				{filterOptions.map((opt) => {
					return (
						<TableRow key={`${opt.name}-filter-row`}>
							<TableCell sx={TableHeadCellSx} align="center" width="5%">
								<Tooltip title={getFilterEnabled(opt.name) ? 'Disable Filter' : 'Enable Filter'} role="tooltip">
									<Checkbox
										checked={getFilterEnabled(opt.name)}
										onChange={(_, check) => {
											setFilterEnabled(opt.name, check);
										}}
										data-testid={`${opt.name}-enable-checkbox`}
										slotProps={{
											input: {
												'aria-label': getFilterEnabled(opt.name) ? `Disable ${opt.label} Filter` : `Enable ${opt.label} Filter`,
											},
										}}
									/>
								</Tooltip>
							</TableCell>
							<TableCell width="20%" sx={TableHeadCellSx}>
								<Typography>{opt.label}</Typography>
							</TableCell>
							<TableCell sx={TableHeadCellSx}>{getFilterComponent(opt)}</TableCell>
						</TableRow>
					);
				})}
			</TableHead>
		</Table>
	);
};

export default FilterTable;
